import React, { Component, createRef } from 'react'
import AutoSizer from 'react-virtualized/dist/commonjs/AutoSizer'
import isEqual from 'lodash/isEqual'
import { MarketsComponent } from './MarketsComponent'
import { MarketsExchangeHeader } from './MarketsExchangeHeader'
import { MobileFilters } from './MobileFilters'
import { getValues, parseSlug } from './helpers'
import { InnerLoader } from 'src/components/InnerLoader'
import Pagination from 'src/components/Pagination'
import { ENTITY, sharedFiltersOptions } from 'src/consts'
import { connect } from 'react-redux'
import {
  getMarketsCurrencySelector,
  getMarketsDataSelector,
  getMarketsFailedStatusSelector,
  getMarketsFetchingStatusSelector,
} from 'src/redux/selectors/markets'
import Router from 'next/router'
import { getMarketsData } from 'src/redux/thunks'
import styles from './styles.module.scss'
import { getCoinsDict } from 'src/redux/selectors'
import { combinedAnalytics } from 'src/utils/common'
import { intl } from 'src/i18n'

const ENTITY_TABLE_CONFIG = {
  [ENTITY.EXCHANGE]: {
    0: {
      justifyContent: 'flex-start',
      key: 'fixed',
      relWidth: 18,
      isSortable: true,
    },
    1: {
      justifyContent: 'flex-start',
      headerName: intl.t(
        'coinDetails.marketsTab.marketsExchangeContainer.pair'
      ),
      mobileHeaderName: intl.t(
        'coinDetails.marketsTab.marketsExchangeContainer.pair'
      ),
      key: 'exchangePair',
      relWidth: 9,
      isSortable: false,
    },
    2: {
      justifyContent: 'flex-end',
      headerName: intl.t(
        'coinDetails.marketsTab.marketsExchangeContainer.volume.desktop'
      ),
      mobileHeaderName: intl.t(
        'coinDetails.marketsTab.marketsExchangeContainer.volume.mobile'
      ),
      key: 'volume',
      relWidth: 14,
      isSortable: true,
    },
    3: {
      justifyContent: 'flex-end',
      headerName: intl.t(
        'coinDetails.marketsTab.marketsExchangeContainer.price'
      ),
      mobileHeaderName: intl.t(
        'coinDetails.marketsTab.marketsExchangeContainer.price'
      ),
      key: 'price',
      relWidth: 17,
      isSortable: true,
    },
    4: {
      justifyContent: 'flex-end',
      headerName: intl.t(
        'coinDetails.marketsTab.marketsExchangeContainer.spread.desktop'
      ),
      mobileHeaderName: intl.t(
        'coinDetails.marketsTab.marketsExchangeContainer.spread.mobile'
      ),
      key: 'spread',
      relWidth: 16.5,
      isSortable: true,
    },
    5: {
      justifyContent: 'flex-end',
      headerName: intl.t(
        'coinDetails.marketsTab.marketsExchangeContainer.volume2.desktop'
      ),
      mobileHeaderName: intl.t(
        'coinDetails.marketsTab.marketsExchangeContainer.volume2.mobile'
      ),
      key: 'volumePercent',
      relWidth: 14,
      isSortable: true,
    },
    6: {
      justifyContent: 'flex-end',
      headerName: intl.t(
        'coinDetails.marketsTab.marketsExchangeContainer.updated'
      ),
      mobileHeaderName: intl.t(
        'coinDetails.marketsTab.marketsExchangeContainer.updated'
      ),
      key: 'updated',
      relWidth: 12,
      isSortable: true,
    },
  },
}

class MarketsExchangeContainer extends Component {
  state = {
    data: [],
    page: 0,
    dataSliceStart: 0,
    dataSliceEnd: 10,
    pageTotal: 0,
    cachedStart: 0,
    cachedEnd: 10,
    cachedPage: 0,
    dataPage: [],
    cachedData: [],
    viewAll: false,
    sortBy: 'volume',
    sortDirection: 'DESC',
    isMobileFiltersPanelOpened: false,
    pair: 'All',
    feeType: 'All',
    onlyVerifiedIsAvailable: this.props.entity === ENTITY.COIN,
    onlyVerified: true,
  }

  async componentDidMount() {
    await this.props.getMarketsData({
      entity: this.props.entity,
      slug: this.props.slug,
    })

    this.resetData()
  }

  async componentDidUpdate(prevProps, prevState) {
    const haveFiltersChanged = prevState.currency !== this.state.currency
    const hasStateChanged =
      this.state.sortBy !== prevState.sortBy ||
      this.state.sortDirection !== prevState.sortDirection ||
      this.state.onlyVerified !== prevState.onlyVerified ||
      haveFiltersChanged
    const isFetchNeeded =
      this.props.entity !== prevProps.entity ||
      this.props.slug !== prevProps.slug

    if (isFetchNeeded) {
      await this.props.getMarketsData({
        entity: this.props.entity,
        slug: this.props.slug,
        currency: this.state.currency,
      })
      if (this.inputRef.current) {
        this.inputRef.current.value = ''
      }
    }
    if (
      hasStateChanged ||
      !isEqual(this.props.marketsData, prevProps.marketsData) ||
      isFetchNeeded
    ) {
      this.resetData()
    }
    if (haveFiltersChanged) {
      this.setSamePage()
    }
  }

  inputRef = createRef()

  setSamePage = data => {
    const { cachedData, cachedStart, cachedEnd } = this.state
    this.setState({
      dataPage: data
        ? data.slice(cachedStart, cachedEnd)
        : cachedData.slice(cachedStart, cachedEnd),
      cachedData: data ? data : cachedData,
    })
  }

  resetData = () => {
    const { cachedData } = this.state
    const generatedData = this.generateData()
    const currentPage = this.setPageData(generatedData)
    this.setState(
      {
        data: generatedData,
        cachedData: generatedData,
        dataPage: currentPage,
      },
      () => {
        const value = this.inputRef.current ? this.inputRef.current.value : ''
        if (value && cachedData.length) {
          this.searchFilter(value)
        }
      }
    )
  }

  setPageData = data => {
    const totalPages = Math.ceil(data.length / 10)
    this.setState({
      pageTotal: totalPages,
      page: 0,
      dataSliceStart: 0,
      dataSliceEnd: 10,
    })
    return totalPages ? data.slice(0, 10) : data
  }

  setCachedData = data => {
    const dataPage = this.setPageData(data)
    this.setState({
      cachedData: data,
      dataPage,
    })
  }

  searchFilter = key => {
    const { data } = this.state
    if (!key) {
      this.setCachedData(data)
      this.setState({
        cachedStart: 0,
        cachedEnd: 10,
        cachedPage: 0,
      })
      return
    }
    const tempData = data.filter(item => {
      const coin = item.coin
      return (
        (coin && coin.name.toLowerCase().includes(key)) ||
        item.exchangePair.toLowerCase().includes(key)
      )
    })
    this.setCachedData(tempData)
    this.setSamePage(tempData)
  }

  handleViewAllClick = () => {
    const { cachedData, viewAll } = this.state
    let tempData = []
    if (!viewAll) {
      tempData = cachedData
    } else {
      tempData = cachedData.slice(0, 10)
    }

    this.setState(prevState => ({
      viewAll: !prevState.viewAll,
      dataPage: tempData,
      page: 0,
      dataSliceStart: 0,
      dataSliceEnd: 10,
      cachedStart: 0,
      cachedEnd: tempData.length,
      cachedPage: 0,
    }))
  }

  handlePage = pageChange => () => {
    const {
      cachedPage,
      cachedStart,
      cachedEnd,
      dataSliceStart,
      dataSliceEnd,
      cachedData,
    } = this.state
    const newStart = cachedPage
      ? cachedStart + pageChange * 10
      : dataSliceStart + pageChange * 10
    const newEnd = cachedPage
      ? cachedEnd + pageChange * 10
      : dataSliceEnd + pageChange * 10
    const newData = cachedData.slice(newStart, newEnd)

    this.setState(prevState => ({
      dataSliceStart: newStart,
      dataSliceEnd: newEnd,
      dataPage: newData,
      page: (prevState.cachedPage || prevState.page) + pageChange,
      cachedStart: newStart,
      cachedEnd: newEnd,
      cachedPage: (prevState.cachedPage || prevState.page) + pageChange,
    }))
  }

  openMobileFilters = () => {
    this.setState({
      isMobileFiltersPanelOpened: true,
    })
  }

  closeMobileFilters = () => {
    this.setState({
      isMobileFiltersPanelOpened: false,
    })
  }

  changeFilter = name => value => {
    this.setState({
      [name]: value,
    })

    const combinedAnalyticsNamesMap = {
      pair: 'Pair',
      feeType: 'Fee type',
      currency: 'Currency',
    }

    combinedAnalytics(
      'Filter',
      'Filter',
      `Interaction:${this.props.entity} page`,
      `${combinedAnalyticsNamesMap[name]} ${value}`
    )
  }

  changeOnlyVerified = () => {
    this.setState(prevState => {
      const onlyVerified = !prevState.onlyVerified

      combinedAnalytics(
        'Filter',
        'Filter',
        `Interaction:${this.props.entity} page`,
        `Only verified ${onlyVerified ? 'On' : 'Off'}`
      )

      return { onlyVerified }
    })
  }

  changeCurrencyFilter = async value => {
    await this.props.getMarketsData({
      entity: this.props.entity,
      slug: this.props.slug,
      currency: value.toLowerCase(),
    })

    this.setState(
      {
        currency: value,
      },
      () => {
        const value = this.inputRef.current ? this.inputRef.current.value : ''
        if (value) {
          this.searchFilter(value)
        }
      }
    )

    combinedAnalytics(
      'Filter',
      'Filter',
      `Interaction:${this.props.entity} page`,
      `Currency ${value}`
    )
  }

  getFilters = () => {
    return [
      {
        name: 'currency',
        labelInside: 'Base currency',
        value: this.state.currency || sharedFiltersOptions.currency[0].value,
        options: sharedFiltersOptions.currency,
        onChangeHandler: this.changeCurrencyFilter,
      },
    ]
  }

  handleSortingChange = sortBy => () => {
    this.setState(prevState => {
      const newState = { sortBy }
      if (prevState.sortBy === sortBy) {
        newState.sortDirection =
          prevState.sortDirection === 'DESC' ? 'ASC' : 'DESC'
      } else {
        newState.sortDirection = 'DESC'
      }

      const combinedAnalyticsNamesMap = {
        index: 'Number',
        base: this.props.entity === ENTITY.COIN ? 'Source' : 'Currency',
        market: 'Pair',
        volume: 'Volume24H',
        price: 'Price',
        spread: 'Spread',
        volumePercent: 'Volume%',
        feeType: 'Fee Type',
        timestamp: 'Updated',
      }
      const combinedAnalyticsName = combinedAnalyticsNamesMap[sortBy]

      combinedAnalytics(
        'Filter',
        'Filter',
        `Interaction:${this.props.entity} page sort`,
        `${combinedAnalyticsName ? combinedAnalyticsName : sortBy}_${
          newState.sortDirection === 'DESC' ? 'Down' : 'Up'
        }`
      )

      return newState
    })
  }

  onCellClickHandler = e => {
    const { location } = this.props
    if (e) {
      e.preventDefault()
      sessionStorage.setItem(
        'lastDataPage',
        `${location.pathname}${location.search}`
      )
      e.target.getAttribute('data-page') &&
        Router.push(e.target.getAttribute('data-page'))
    }
  }

  getUpdatedValue = marketTs => {
    const nowTs = Math.floor(Date.now() / 1000)
    const diffInSeconds = nowTs - marketTs

    if (diffInSeconds < 300) {
      return intl.t('coinDetails.marketsTab.marketsExchangeContainer.recently')
    } else if (diffInSeconds < 3600) {
      return `${Math.round(diffInSeconds / 60)} ${intl.t(
        'coinDetails.marketsTab.marketsExchangeContainer.minutesAgo'
      )}`
    } else if (diffInSeconds < 86400) {
      const marketTime = new Date(marketTs * 1000)
      return `${marketTime.getHours()}:${marketTime.getMinutes()}`
    } else {
      const marketTime = new Date(marketTs * 1000)
      return `${marketTime.getDate()}.${
        marketTime.getMonth() + 1
      } ${marketTime.getHours()}:${marketTime.getMinutes()}`
    }
  }

  getSortedData = marketsData => {
    const { sortBy, sortDirection } = this.state

    const sortCallback = (a, b) => {
      const isSortByString = ['exchange', 'market', 'source', 'base'].some(
        key => sortBy === key
      )
      if (sortDirection === 'ASC') {
        return isSortByString
          ? a[sortBy].localeCompare(b[sortBy])
          : a[sortBy] - b[sortBy]
      }
      return isSortByString
        ? b[sortBy].localeCompare(a[sortBy])
        : b[sortBy] - a[sortBy]
    }

    return marketsData.sort(sortCallback)
  }

  generateData = () => {
    const { marketsCurrency, marketsData } = this.props

    const filteredData = marketsData.map((item, i) => ({
      ...item,
      index: i + 1,
    }))

    const result = this.getSortedData(filteredData).map(market => {
      const { volume, price, volumePercent, spread } = getValues(
        market,
        marketsCurrency.toUpperCase()
      )

      const [, base] = parseSlug(market.base)
      const [, quote] = parseSlug(market.quote)
      const exchangePair =
        !!base && !!quote ? `${base.toUpperCase()}/${quote.toUpperCase()}` : '?'

      return {
        ...market,
        exchangePair,
        spread,
        volume,
        price,
        volumePercent: `${volumePercent}%`,
        updated: this.getUpdatedValue(market.time),
      }
    })

    return result
  }

  render() {
    const {
      isFailed,
      isFetching,
      isMobile,
      name,
      entity,
      exchangeLink,
      coinsDict,
      slug,
      symbol,
    } = this.props
    const {
      data,
      cachedPage,
      pageTotal,
      dataPage,
      viewAll,
      sortDirection,
      sortBy,
      isMobileFiltersPanelOpened,
      onlyVerifiedIsAvailable,
      onlyVerified,
    } = this.state

    let tableHeight = '610px'

    if (data.length < 10 && data.length > 0) {
      tableHeight = (data.length + 1) * 50 + 60
    }

    if (data.length === 0 && !isFetching) {
      tableHeight = '200px'
    }

    const paginationStyles = {
      display: 'flex',
      justifyContent: isMobile ? 'center' : 'flex-end',
      width: '100%',
      marginTop: '15px',
    }

    return (
      <div className={styles.root}>
        {isMobile && (
          <MobileFilters
            isMobileFiltersPanelOpened={isMobileFiltersPanelOpened}
            closeMobileFilters={this.closeMobileFilters}
            onlyVerifiedIsAvailable={onlyVerifiedIsAvailable}
            onlyVerified={onlyVerified}
            changeOnlyVerified={this.changeOnlyVerified}
            filters={this.getFilters()}
          />
        )}
        <div
          style={{
            height: tableHeight,
            marginBottom: isMobile ? '130px' : '70px',
          }}
          className='MarketsContainer'
        >
          <MarketsExchangeHeader
            openMobileFilters={this.openMobileFilters}
            onSearch={key => this.searchFilter(key.toLowerCase())}
            filters={this.getFilters()}
            coinName={name}
            isMobile={isMobile}
            onlyVerifiedIsAvailable={onlyVerifiedIsAvailable}
            onlyVerified={onlyVerified}
            changeOnlyVerified={this.changeOnlyVerified}
            inputRef={this.inputRef}
          />
          <AutoSizer style={{ height: 'auto' }}>
            {({ width, height }) => {
              if (isFetching) return <InnerLoader style={{ top: 80 }} />
              if (!dataPage || dataPage.length === 0)
                return <div className='MarketsNoData'>No data</div>

              return (
                <MarketsComponent
                  isFailed={isFailed}
                  isFetching={isFetching}
                  width={width}
                  height={height - 60}
                  isMobile={isMobile}
                  data={dataPage}
                  sortDirection={sortDirection}
                  handleSortingChange={this.handleSortingChange}
                  sortBy={sortBy}
                  tableConfig={ENTITY_TABLE_CONFIG[entity]}
                  entity={entity}
                  onCellClick={this.onCellClickHandler}
                  exchangeLink={exchangeLink}
                  dataDict={coinsDict}
                  slug={slug}
                />
              )
            }}
          </AutoSizer>
          <div style={paginationStyles}>
            <Pagination
              count='10'
              viewAll={viewAll}
              page={cachedPage}
              pageTotal={pageTotal}
              onNext={this.handlePage(1)}
              onPrev={this.handlePage(-1)}
              onAll={this.handleViewAllClick}
              position='right'
            />
          </div>
        </div>
      </div>
    )
  }
}

export default connect(
  (state, ownProps) => ({
    marketsData: getMarketsDataSelector(state, ownProps.entity),
    marketsCurrency: getMarketsCurrencySelector(state),
    isFetching: getMarketsFetchingStatusSelector(state),
    isFailed: getMarketsFailedStatusSelector(state),
    // location: getCurrentLocation(state),
    coinsDict: getCoinsDict(state),
  }),
  { getMarketsData }
)(MarketsExchangeContainer)
